import { getChainInfo, Network, SupportedNetwork, SUPPORTED_NETWORKS } from '@spritz-finance/sdk'
import {
    Body,
    CaretSvg,
    colors,
    Dropdown,
    Icon,
    Margin,
    NotAllowedSvg,
    Spacer,
} from '@spritz-finance/ui'
import styled from 'styled-components'
import { NETWORK_SWITCHER_OPENED } from '../../analytics/events'
import { Row } from '../../components/Layout/Row'
import { useAnalytics } from '../../context/useAnalytics'
import { noSelect } from '../../utils/noSelect'
import { networkColors } from '../networkColors'
import { useSwitchNetwork } from '../utils/useSwitchNetwork'
import { useWeb3Connection } from '../Web3Provider'
import { NetworkLogo } from './NetworkLogo'

const LogoWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
`

const DropdownItem = styled(Dropdown.Item)`
    &:hover {
        span {
            color: ${colors.black} !important;
        }
    }
`

const ActiveCircle = styled.div<{ $network: SupportedNetwork }>`
    justify-content: center;
    align-items: center;

    > div {
        height: 8px;
        width: 8px;
        background-color: ${({ $network }) => networkColors[$network].bgDark};
        border-radius: 50%;
    }
`

const ActiveItem = styled.div<{ $network: SupportedNetwork }>`
    ${noSelect};
    display: flex;
    padding: ${Margin.Small};
    align-items: center;
    justify-content: space-between;
    background: ${({ $network }) => `var(--surface-3, ${networkColors[$network].bgLight})`};
    border-radius: 10px;
    margin: 8px;
    padding-left: 8px;
`

const NetworkSwitchWrapper = styled.div<{ $mobile: boolean }>`
    padding: ${({ $mobile }) => ($mobile ? '6px 8px 6px 10px' : '10px 12px 10px 14px')};
    &:hover {
        cursor: pointer;
    }
`

const ImageSizeOverride = {
    [Network.Ethereum]: 22,
    [Network.Binance]: 18,
}

const Container = styled(Row)``

export const NetworkSwitcher = ({ mobileStyle = false }: { mobileStyle?: boolean }) => {
    const { trackAnalyticsEvent } = useAnalytics()
    const { connectedNetwork } = useWeb3Connection()
    const { switchNetwork } = useSwitchNetwork()

    const networkTitle = !mobileStyle
    const currentChainInfo = connectedNetwork ? getChainInfo(connectedNetwork) : null

    return (
        <Container align="center">
            <Dropdown.Menu
                minWidth={218}
                align="start"
                arrow={false}
                offset={10}
                onOpenChange={(val) => {
                    if (val) trackAnalyticsEvent(NETWORK_SWITCHER_OPENED)
                }}
                anchor={
                    <NetworkSwitchWrapper $mobile={mobileStyle}>
                        <Row align="center">
                            {connectedNetwork ? (
                                <NetworkLogo
                                    network={connectedNetwork}
                                    size={ImageSizeOverride[connectedNetwork] ?? 16}
                                />
                            ) : (
                                <Icon icon={NotAllowedSvg} tint={colors.medGrey} />
                            )}
                            {networkTitle && (
                                <>
                                    <Spacer width={8} />
                                    <Body.SmBold
                                        size={16}
                                        color={
                                            currentChainInfo
                                                ? `var(--text-primary, ${colors.black})`
                                                : colors.charcoal
                                        }
                                    >
                                        {currentChainInfo?.name ?? 'Unsupported'}
                                    </Body.SmBold>
                                    <Spacer width={4} />
                                </>
                            )}
                            <Icon icon={CaretSvg} size={24} />
                        </Row>
                    </NetworkSwitchWrapper>
                }
            >
                {SUPPORTED_NETWORKS.map((network) => {
                    const active = network === connectedNetwork
                    const networkInfo = getChainInfo(network)

                    if (active)
                        return (
                            <ActiveItem key={network} $network={network}>
                                <Row>
                                    <LogoWrapper>
                                        <NetworkLogo
                                            network={network}
                                            size={ImageSizeOverride[network] ?? 16}
                                        />
                                    </LogoWrapper>
                                    <Spacer width={8} />
                                    <Body.SmBold size={16}>{networkInfo.name}</Body.SmBold>
                                </Row>
                                <Spacer width={8} />
                                <ActiveCircle $network={network}>
                                    <div />
                                </ActiveCircle>
                            </ActiveItem>
                        )
                    return (
                        <DropdownItem
                            hoverEffect={false}
                            key={network}
                            onClick={() => switchNetwork(network)}
                        >
                            <Row align="space-between">
                                <LogoWrapper>
                                    <NetworkLogo
                                        network={network}
                                        size={ImageSizeOverride[network] ?? 16}
                                    />
                                </LogoWrapper>
                                <Spacer width={8} />
                                <Body.SmBold size={16} color={colors.medGrey}>
                                    {networkInfo.name}
                                </Body.SmBold>
                            </Row>
                        </DropdownItem>
                    )
                })}
            </Dropdown.Menu>
        </Container>
    )
}
